import { Checkbox, FormControlLabel, InputLabel } from '@mui/material';
import cx from 'classnames';
import { current } from 'immer';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import FrequencyField from 'js/components/elements/FrequencyField';
import Menu from 'js/components/elements/menu/Menu';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import JSONUtils from 'js/lib/JSONUtils';
import UIUtils from 'js/lib/UIUtils';
import { convertToValidDBColumnName } from 'js/lib/ValidationUtils';
import { ReactComponent as CodeRoundIcon } from 'new/components/assets/codeRounded.svg';
import useCDC from 'new/components/flows/types/cdc/CreateCDCFlow/CDCFlow/useCDC';
import {
  mapToSinkConfig,
  parseSinkConfig,
} from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/sinkConfigMapper';
import {
  mapToSource,
  parseSourceConfig,
} from 'new/components/flows/types/cdc/CreateCDCFlow/ResourceSetupPage/sourceConfigMapper';
import flowTypes from 'new/components/flows/types/flowTypes';
import { CodeEditorMode } from 'new/elements/CodeEditorMode/CodeEditorMode';
import { Databases } from 'new/elements/flows/Databases';
import { MappingType } from 'new/elements/flows/Databases/useDatabases';
import InfoIconInline from 'new/elements/InfoIconInline/InfoIconInline';
import { ControlProps, SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { Stack } from 'new/elements/Stack';
import { Switch } from 'new/elements/Switch';
import { Tabs } from 'new/elements/Tabs';
import { TabProps } from 'new/elements/Tabs/Tabs';
import { TextField } from 'new/elements/TextField';
import Typography from 'new/elements/Typography';
import { UiMode } from 'new/constants/uiMode';
import useProbeTree from 'new/hooks/adapters/useProbeTree';
import { useAutoHeight } from 'new/hooks/useAutoHeight';
import { useDistance } from 'new/hooks/useDistance';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import usePaging from 'new/state/paging/repository/usePaging';
import { isUiModeCode } from 'new/utils/uiMode';
import React, { PropsWithChildren, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useImmer } from 'use-immer';
import classes from './DatabaseSetup.module.scss';

interface DatabaseSetupProps {
  source: any;
  sink: any;
  onConfigUpdated: (config: any) => void;
  credentialId: number;
  resourceType: 'SOURCE' | 'SINK';
  resourceConfig?: any;
  onRequestProbeTree: (node: any) => void;
  onClickEditConfig?: () => void;
  connectorType: string;
  parentScrollRef?: RefObject<HTMLDivElement>;
  onDiscardAdvancedForm: () => void;
  onSubmitAdvancedForm: (resourceConfig: any) => void;
  initialMapping?: MappingType;
  nexset?: any;
}

const SectionInfo = ({ children }) => {
  return <div className={classes.sectionInfo}>{children}</div>;
};

const Slot = ({
  children,
  alignment,
  className,
}: PropsWithChildren<{ alignment?: string; className?: string }>) => {
  return (
    <div
      className={cx(classes.sectionInfoSlot, className, {
        [classes['sectionInfoSlot_' + alignment]]: !!alignment,
      })}
    >
      {children}
    </div>
  );
};

const SectionInfoTitle = ({
  children,
  tooltipTitle,
  htmlFor,
}: PropsWithChildren<{ tooltipTitle?: string; htmlFor?: string }>) => {
  return (
    <Stack className={classes.sectionInfoTitle}>
      <InputLabel htmlFor={htmlFor}>
        <Typography variant="body2">{children}</Typography>
      </InputLabel>
      {tooltipTitle ? <InfoIconInline title={tooltipTitle} /> : null}
    </Stack>
  );
};

const schemaDetectionControls = [
  {
    label: 'Automatic',
    value: 'auto',
  },
  {
    label: 'Select by name',
    value: 'name',
  },
] as ControlProps[];

const schemaDetectionSinkControls = [
  {
    label: 'Automatic',
    value: 'auto',
  },
  {
    label: 'Customize',
    value: 'name',
  },
] as ControlProps[];

const sinkTabs = [
  { label: 'Streams', value: 'streams' },
  { label: 'DestinationDB', value: 'db' },
] as TabProps[];

const DestinationInfoSection = ({ setConfig, config }) => {
  const { source, selectedConnection } = useCDC();
  const onChangeField = (converter?: (value: string, connection: string) => string) => (e) => {
    const { name, value, checked, type } = e.target as HTMLInputElement;
    if (type === 'checkbox') {
      setConfig((config) => {
        config[name] = checked;
      });
      return;
    }
    const validatedValue = converter ? converter(value, selectedConnection) : value;
    setConfig((config) => {
      config[name] = validatedValue;
      e.target.value = validatedValue;
    });
  };

  return (
    <SectionInfo>
      <Slot>
        <SectionInfoTitle
          htmlFor="namespace"
          tooltipTitle="Name of the database where the tables will be created"
        >
          Destination namespace
        </SectionInfoTitle>
        <TextField
          id="namespace"
          name="namespace"
          data-testid="sink-namespace"
          defaultValue={config.namespace}
          onChange={onChangeField()}
          placeholder="Same as Source (default)"
          fullWidth
        />
      </Slot>
      <Slot>
        <SectionInfoTitle htmlFor="tablePrefix" tooltipTitle="Name used to prefix created tables">
          Destination table prefix
        </SectionInfoTitle>
        <TextField
          id="tablePrefix"
          name="tablePrefix"
          data-testid="sink-table-prefix"
          defaultValue={config.tablePrefix}
          onChange={onChangeField(convertToValidDBColumnName)}
          placeholder=""
          fullWidth
        />
      </Slot>
      <Slot>
        <SectionInfoTitle htmlFor="tableSuffix">Destination table suffix</SectionInfoTitle>
        <TextField
          id="tableSuffix"
          name="tableSuffix"
          data-testid="sink-table-suffix"
          defaultValue={config.tableSuffix}
          onChange={onChangeField(convertToValidDBColumnName)}
          fullWidth
        />
      </Slot>
      <Slot>
        <div
          className={classes.sectionInfoTitle_inline}
          style={{ marginRight: 'var(--spacing-lg)' }}
        >
          <SectionInfoTitle>
            <span style={{ color: 'var(--color-greyscale-700)' }}>Capture change events</span>
          </SectionInfoTitle>
        </div>
        <Stack direction="row" alignItems="center" gap="var(--spacing-md)">
          <FormControlLabel
            htmlFor="captureDelete"
            control={
              <Switch
                id="captureDelete"
                name="captureDelete"
                checked={config?.captureDelete}
                onChange={onChangeField()}
                color="primary"
              />
            }
            label={<Typography style={{ color: 'var(--color-greyscale-700)' }}>Delete</Typography>}
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <Switch
                disabled
                checked={source?.source_config?.['cdc.table.config']?.['capture.create']}
                color="primary"
              />
            }
            label={<Typography style={{ color: 'var(--color-greyscale-700)' }}>Create</Typography>}
            labelPlacement="start"
          />
          <InfoIconInline
            placement="top"
            title="Create events are off by default for destinations in CDC flows. This option is disabled and cannot be enabled."
          />
        </Stack>
      </Slot>
      <Slot className={classes.sectionInfoSlot_right}>
        <Stack direction="row" alignItems="center" gap="var(--spacing-md)">
          <FormControlLabel
            htmlFor="trackerEnabled"
            control={
              <Switch
                id="trackerEnabled"
                name="trackerEnabled"
                checked={!!config.trackerEnabled}
                onChange={onChangeField()}
                color="primary"
              />
            }
            label={
              <Typography style={{ color: 'var(--color-greyscale-700)' }}>Track lineage</Typography>
            }
            labelPlacement="start"
          />
          <Stack width="290px">
            <TextField
              fullWidth
              defaultValue={config.trackerName}
              name="trackerName"
              data-testid="trackername-input"
              onChange={onChangeField()}
              placeholder="__nexla_tracker_id (default)"
              disabled={!config.trackerEnabled}
            />
          </Stack>
        </Stack>
      </Slot>
    </SectionInfo>
  );
};

const SourceInfoSection = ({ setConfig, config, cdcSourceConfigFields, onChangeSwitch }) => {
  const isCDCv2Enabled = useFeatureFlag('UI_ENABLE_CDC_V2');
  const cdcModeOptions = cdcSourceConfigFields.find((field) => field.name === 'cdc.mode')?.options;
  const selectedCDCModeOption = getCurrentSelectedOption(
    cdcModeOptions,
    config.isCDCMode ? 'multi_table' : 'database_read',
  );

  const onChangeCapture = (event) => {
    const { name, checked } = event.target;
    setConfig((config) => {
      config[name] = checked;
    });
  };

  const onChangeFrequency = (value) => {
    setConfig((config) => {
      config.frequency = value;
    });
  };

  const onChangeCDCMode = (selectedOption) => {
    const value = getNewSelectionValue(selectedOption, true);
    setConfig((config) => {
      config.isCDCMode = value === 'multi_table';
    });
  };

  return (
    <SectionInfo>
      {isCDCv2Enabled && cdcModeOptions?.length > 1 && (
        <>
          <Stack direction="row" alignItems="center" gap="var(--spacing-lg)">
            <Typography variant="body1">Replication Method</Typography>
            <UnsortedSelect
              options={cdcModeOptions}
              components={{ Input: SelectInput, Option: SelectOption }}
              isClearable={false}
              isSearchable
              backspaceRemovesValue={false}
              value={selectedCDCModeOption}
              onChange={onChangeCDCMode}
              styles={{
                ...baseSelectStyles,
                container: (styles) => ({ ...styles, flexGrow: 1 }),
              }}
            />
          </Stack>
          <Slot />
        </>
      )}
      {config.isCDCMode && (
        <>
          <Slot>
            <Stack direction="row" alignItems="center" gap="var(--spacing-lg)">
              <div
                className={classes.sectionInfoTitle_inline}
                style={{ marginRight: 'var(--spacing-lg)' }}
              >
                <SectionInfoTitle>
                  <span style={{ color: 'var(--color-greyscale-700)' }}>Capture change events</span>
                  <InfoIconInline
                    placement="top"
                    title="Create events are always captured for CDC sources. This is required to detect newly inserted rows and cannot be turned off."
                  />
                </SectionInfoTitle>
              </div>
              <Stack direction="row" alignItems="center" gap="12px">
                <FormControlLabel
                  htmlFor="captureDelete"
                  control={
                    <Switch
                      id="captureDelete"
                      name="captureDelete"
                      checked={!!config['captureDelete']}
                      onChange={onChangeCapture}
                      color="primary"
                    />
                  }
                  label={
                    <Typography style={{ color: 'var(--color-greyscale-700)' }}>Delete</Typography>
                  }
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <Switch
                      disabled
                      name="captureCreate"
                      checked
                      onChange={onChangeCapture}
                      color="primary"
                    />
                  }
                  label={
                    <Typography style={{ color: 'var(--color-greyscale-700)' }}>Create</Typography>
                  }
                  labelPlacement="start"
                />
              </Stack>
            </Stack>
          </Slot>
          <Slot />
          <Slot className={cx(classes.sectionInfoSlot_alignStart, classes.sectionInfoSlot_bottom)}>
            <FormControlLabel
              control={
                <Switch
                  id="snapshotEnabled"
                  name="snapshotEnabled"
                  checked={!!config['snapshotEnabled']}
                  onChange={onChangeSwitch}
                  color="primary"
                />
              }
              label="Ingest existing data"
              labelPlacement="start"
            />
          </Slot>
          <Slot />
        </>
      )}
      <Slot className={classes.sectionInfoSlot_full}>
        <SectionInfoTitle>Replication frequency</SectionInfoTitle>
        <div className={classes.frequencyContainer}>
          <FrequencyField value={config.frequency} onChange={onChangeFrequency} />
        </div>
      </Slot>
    </SectionInfo>
  );
};

const Configuration = ({
  resourceType,
  setConfig,
  config,
  tab,
  credentialId,
  source,
  sourceConnector,
  sinkConnector,
  onRequestProbeTree,
}) => {
  if (tab !== 'streams') return null;
  const { schemaMode, autoDetectNewTables } = config;

  const { databases } = useProbeTree(credentialId);
  const { databases: sourceFullDatabases } = useProbeTree(
    source?.data_credentials || source?.data_credentials_id,
  );

  const sourceDatabases = useMemo(() => {
    if (!source) return [];
    if (resourceType === 'SOURCE') return sourceFullDatabases;
    if (!sourceFullDatabases || !sourceFullDatabases.length) return [];
    const exclude_tables = source.source_config?.['cdc.table.config']?.['exclude.tables'];
    const include_tables = source.source_config?.['cdc.table.config']?.['include.tables'];
    if (!exclude_tables && !include_tables) {
      return sourceFullDatabases;
    }
    const database = { ...sourceFullDatabases[0] };
    database.children = sourceFullDatabases?.[0]?.children?.filter((table) => {
      if (exclude_tables) {
        return !exclude_tables.includes(table.name);
      }
      return include_tables.includes(table.name);
    });
    return [database];
  }, [sourceFullDatabases, source]);

  useEffect(() => {
    // when editing a source, initialize the resourceConfig by calling setConfig
    // which in turn will call mapToSource
    // TODO:(limiting this to only the auto-detect mode for now in order to contain
    // the impact of this change but it should probably happen in all cases)
    if (
      resourceType === 'SOURCE' &&
      source &&
      !config.isCDCMode &&
      config.autoDetectNewTables &&
      schemaMode === 'name'
    ) {
      setConfig((config) => {
        config.schemaMode = config.schemaMode;
      });
    }
  }, []);

  const onControlChange = (event, value) => {
    setConfig((config) => {
      config.schemaMode = value;
    });
  };

  const onSelectionChange = (selection) => {
    setConfig((config) => {
      config.selection = selection;
    });
  };

  const onMappingChange = (mapping) => {
    setConfig((config) => {
      config.mapping = mapping;
    });
  };

  const onResyncSelectionChange = (resyncSelection) => {
    setConfig((config) => {
      config.resyncSelection = resyncSelection;
    });
  };

  const onTableFiltersChange = (tableFilters) => {
    setConfig((config) => {
      config.tableFilters = tableFilters;
    });
  };

  const onColumnSelectionsChange = (columnSelections) => {
    setConfig((config) => {
      config.columnSelections = columnSelections;
    });
  };

  const onSyncModesChange = (syncModes) => {
    setConfig((config) => {
      config.syncModes = syncModes;
    });
  };

  const onChangeAutoDetectNewTables = (event) => {
    setConfig((config) => {
      config.autoDetectNewTables = event.target.checked;
    });
  };

  return (
    <>
      <div className={cx(classes.sectionInfo, classes.sectionInfoDatabase)}>
        <div className={classes.selectionModeRow} data-testid="cdc-source-selection-row">
          <Stack>
            <SectionInfoTitle>
              {resourceType === 'SINK' ? 'Stream selection mode' : 'Schema selection mode'}
            </SectionInfoTitle>
          </Stack>
          <SegmentedControl
            controls={
              resourceType === 'SINK' ? schemaDetectionSinkControls : schemaDetectionControls
            }
            defaultValue="auto"
            value={schemaMode}
            onControlChange={onControlChange}
          />
          {resourceType === 'SOURCE' && !config.isCDCMode && schemaMode === 'name' && (
            <Stack flexGrow={1} alignItems="flex-end">
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={onChangeAutoDetectNewTables}
                    checked={autoDetectNewTables || false}
                    color="primary"
                    size="small"
                  />
                }
                label="Automatically include new tables"
                labelPlacement="end"
                style={{ marginRight: 0 }}
              />
            </Stack>
          )}
        </div>
      </div>
      <Databases
        databases={resourceType === 'SINK' ? sourceDatabases : databases}
        credentialId={
          resourceType === 'SINK'
            ? source?.data_credentials || source?.data_credentials_id
            : credentialId
        }
        initialMapping={config.mapping}
        initialSelection={config.selection}
        initialResyncSelection={config.resyncSelection}
        initialTableFilters={config.tableFilters}
        initialColumnSelections={config.columnSelections}
        initialSyncModes={config.syncModes}
        sourceConnector={sourceConnector}
        sinkConnector={sinkConnector}
        onSelectedTablesChange={onSelectionChange}
        onMappingChange={onMappingChange}
        onResyncSelectionChange={onResyncSelectionChange}
        onTableFiltersChange={onTableFiltersChange}
        onColumnSelectionsChange={onColumnSelectionsChange}
        onSyncModesChange={onSyncModesChange}
        isDestination={resourceType === 'SINK'}
        needsSelection={schemaMode === 'name'}
        isCDCMode={config.isCDCMode}
      />
    </>
  );
};

const DataBrowser = ({ tab, credentialId, resourceType }) => {
  const { databases } = useProbeTree(credentialId);
  if (tab !== 'db') return null;
  return (
    <>
      <div className={classes.databaseTitle}>
        <SectionInfoTitle>Browse data warehouse</SectionInfoTitle>
      </div>
      <Databases
        databases={databases}
        credentialId={credentialId}
        isDestination={resourceType === 'SINK'}
      />
    </>
  );
};

const sourceInitialState = {
  schemaMode: 'auto',
  criteriaBehavior: 'include',
  tableFilters: {},
  resyncSelection: {},
  columnSelections: {},
};

const sinkInitialState = {
  schemaMode: 'auto',
  criteriaBehavior: 'include',
};

export const DatabaseSetup = (props: DatabaseSetupProps) => {
  const { connectorType, onDiscardAdvancedForm, credentialId, nexset } = props;
  const [showAdvancedMode, setShowAdvancedMode] = useState(isUiModeCode(props?.resourceConfig));

  const topRef = useRef();
  const editorHeight = useDistance(topRef);
  const { databases } = useProbeTree(credentialId);
  const allAvailableTables = databases?.[0]?.children?.map((table) => table.name) || [];

  const vendors = useSelector((state: any) => state.entities.vendors);
  const { getSourceConfigFields } = usePaging();
  const cdcSourceConfigFields = getSourceConfigFields(connectorType, flowTypes.cdc);
  const cdcSourceInitialConfig = UIUtils.configWithMissingDefaults(
    sourceInitialState,
    cdcSourceConfigFields,
  );
  cdcSourceInitialConfig.isCDCMode = cdcSourceInitialConfig['cdc.mode'] === 'multi_table';
  const [resourceConfig, setImmerConfig] = useImmer(
    props.resourceConfig ||
      (props.resourceType === 'SOURCE' ? cdcSourceInitialConfig : sinkInitialState),
  );
  const [tab, setTab] = useState('streams');

  useEffect(() => {
    if (props.resourceConfig) {
      setImmerConfig(() => props.resourceConfig);
    }
  }, [props.resourceConfig]);

  const onTabChange = (e, value) => {
    setTab(value);
  };

  const setConfig = (updater) => {
    setImmerConfig((config) => {
      updater(config);
      props.onConfigUpdated(current(config));
    });
  };

  const onChangeSwitch = (e) => {
    const { name, checked } = e.target;
    setConfig((resourceConfig) => {
      resourceConfig[name] = checked;
    });
  };

  const sourceConnector = (() => {
    const vendorId = props.source?.vendor_id;
    if (vendorId && vendors[vendorId]) {
      return vendors[vendorId].name;
    }
    return props.source?.connector_type || props.connectorType;
  })();

  const sinkConnector = (() => {
    if (props.resourceType === 'SINK') return props.connectorType;
  })();

  const onClickEditConfig = () => {
    setShowAdvancedMode(true);
  };

  const onCloseAdvancedMode = () => {
    if (isUiModeCode(resourceConfig)) {
      if (typeof onDiscardAdvancedForm === 'function') {
        onDiscardAdvancedForm();
      }
      return;
    }
    setShowAdvancedMode(false);
  };

  const onSaveAdvancedMode = (configString: string) => {
    const parse = props.resourceType === 'SOURCE' ? parseSourceConfig : parseSinkConfig;
    if (JSONUtils.isJSON(configString)) {
      setImmerConfig(() => {
        const newConfig = {
          ...parse(JSON.parse(configString)),
          ['ui.mode']: UiMode.CODE,
        };
        if (typeof props.onSubmitAdvancedForm === 'function') {
          props.onSubmitAdvancedForm(newConfig);
        }
        return newConfig;
      });
    }
  };

  const onSwitchUiMode = () => {
    setConfig((config) => {
      config['ui.mode'] = UiMode.VIEW;
    });
    setShowAdvancedMode(false);
  };

  return (
    <div className={`${classes.setupContainer} nexla-ds-scope`}>
      <HeaderMetaConfiguration
        connection={props.connectorType}
        isDestination={props.resourceType === 'SINK'}
        nexsetId={nexset?.id}
        enableInputFields={true}
        dense
        name={resourceConfig?.name || ''}
        description={resourceConfig?.description || ''}
        sourceConnectorType={sourceConnector}
        onChange={({ name, value }) => {
          if (!name) return;
          setConfig((cfg) => {
            cfg[name] = value;
          });
        }}
        sourceOrDestinationChipChildren={
          <Menu
            horizontalDots
            items={[
              {
                label: 'View JSON Config',
                icon: <CodeRoundIcon />,
                onClick: onClickEditConfig,
              },
            ]}
          />
        }
      />
      <Stack
        flex={1}
        position="relative"
        id="databaseSetupContainer"
        className={classes.databaseSetupContainer}
        ref={useAutoHeight<HTMLDivElement>(0, 0, Number.POSITIVE_INFINITY)}
      >
        {props.resourceType === 'SOURCE' ? (
          <SourceInfoSection
            cdcSourceConfigFields={cdcSourceConfigFields}
            config={resourceConfig}
            setConfig={setConfig}
            onChangeSwitch={onChangeSwitch}
          />
        ) : (
          <DestinationInfoSection setConfig={setConfig} config={resourceConfig} />
        )}
        <div className={classes.databaseSection}>
          {props.resourceType === 'SINK' ? (
            <div className={classes.tabsContainer}>
              <Tabs tabs={sinkTabs} value={tab} onChange={onTabChange} />
            </div>
          ) : null}
          <Configuration
            source={props.source}
            onRequestProbeTree={props.onRequestProbeTree}
            credentialId={props.credentialId}
            setConfig={setConfig}
            config={resourceConfig}
            sourceConnector={sourceConnector}
            sinkConnector={sinkConnector}
            resourceType={props.resourceType}
            tab={tab}
          />
          <DataBrowser
            credentialId={props.credentialId}
            tab={tab}
            resourceType={props.resourceType}
          />
        </div>
        <CodeEditorMode
          cancelable={!isUiModeCode(resourceConfig)}
          editorHeight={editorHeight - 100}
          open={showAdvancedMode}
          config={(() => {
            const base =
              props.resourceType === 'SOURCE'
                ? mapToSource(resourceConfig, allAvailableTables)
                : mapToSinkConfig(resourceConfig);

            // NEX-21470: when the user is transitioning from VIEW into CODE
            // mode (no ui.mode flag yet on the resource), surface the
            // probe-derived `database` and `table` defaults in the drawer so
            // the user can see and edit them. Without this they're silently
            // injected at save time in `useResourceCreation.createNewSource`
            // /`createNewSink`. Display-only enrichment: form state and
            // view-mode behavior are not affected. Once the resource is in
            // code mode, the existing config is shown verbatim.
            if (isUiModeCode(resourceConfig)) return base;
            const innerKey = props.resourceType === 'SOURCE' ? 'source_config' : 'sink_config';
            const inner = { ...(base as any)[innerKey] };
            const probedDb = databases?.[0]?.name;
            const probedTable = databases?.[0]?.children?.[0]?.name;
            if (probedDb && inner.database === undefined) {
              inner.database = probedDb;
            }
            if (props.resourceType === 'SOURCE' && probedTable && inner.table === undefined) {
              inner.table = probedTable;
            }
            return { ...(base as any), [innerKey]: inner };
          })()}
          onClose={onCloseAdvancedMode}
          onSave={onSaveAdvancedMode}
          onSwitchUiMode={onSwitchUiMode}
          connectorType={connectorType}
        />
      </Stack>
    </div>
  );
};
